/**
 * Variables of the prepared data that are not in the questionnaire: made by
 * the preparation, or in the data file itself (in a project without a
 * questionnaire, every variable of the data). The codebook gives types and
 * labels only (facts.md, fact 9), so only the shapes the compiler already
 * turns into honest tables are offered: a table of yes or no fields, a yes or
 * no field, a number, a table of tables of yes or no fields, and a table of
 * numbers. Each becomes a catalogue question with `source: "data"`, so the
 * compiler and the editors need no second path for it.
 *
 * The codebook is walked to any depth: a container that is none of those
 * shapes (mixed, or deeper) is not offered, but the shapes below it are,
 * each under its full codebook path as its label ("Overall Brand NPS//Bank
 * 1//score"), which is what a script writes between `<<` and `>>`: the paths,
 * filters and banner groups of such a variable need no rule of their own.
 *
 * Containers side by side under such a container (one per bank), none of
 * them a shape of its own, that hold the same field in the same shape also
 * give one grid of that field, a row per container ("Overall Brand NPS:
 * score"). Its label is a name only, never a path (`siblings` marks it);
 * every path it has is a row's.
 */
import { HierarchicalDataEntry } from "types/engineOutput.types"
import { unsafeLabel } from "../compiler/names"
import {
  CatalogueInstance,
  CatalogueOption,
  CatalogueQuestion,
  QuestionType,
} from "../types/catalogue"

/** Every name the tab spec creates in the session starts with it. */
const OWN_PREFIX = "__ts_"
/**
 * The helper fields a console Transform pipeline creates (`<<Boolean//True>>`
 * and `Blank`, see stores/queryBuilderStore/scriptEmission.ts): never
 * variables of their own.
 */
const CONSOLE_HELPERS = ["Boolean", "Blank"]
const NUMERIC_TYPES = ["INT", "REAL"]
/** The engine names the number of a range question `<label> - value`. */
const VALUE_SUFFIX = " - value"

const isEntry = (v: unknown): v is HierarchicalDataEntry =>
  typeof v === "object" && v !== null && typeof (v as { label?: unknown }).label === "string"

/** The entries under a TABLE; a leaf's array of values is not children. */
const childrenOf = (e: HierarchicalDataEntry): HierarchicalDataEntry[] =>
  Array.isArray(e.value) ? e.value.filter(isEntry) : []

const isBool = (e: HierarchicalDataEntry): boolean => e.type === "BOOL"
const isNumber = (e: HierarchicalDataEntry): boolean => NUMERIC_TYPES.includes(e.type)
/** A TABLE with at least one child, every child a yes or no field. */
const isTableOfLeaves = (e: HierarchicalDataEntry): boolean => {
  const kids = childrenOf(e)
  return e.type === "TABLE" && kids.length > 0 && kids.every(isBool)
}

const labelsOf = (e: HierarchicalDataEntry): string[] => childrenOf(e).map((k) => k.label)

/**
 * The value fields of range questions among `entries` (one list: the top
 * level, or the children of one TABLE): a number `<L> - value` beside a
 * TABLE `<L>`. It holds the codes of the answer, exception codes included
 * (facts.md, fact 5), so it is never a variable or a cell of its own, and a
 * TABLE's shape is judged without it.
 */
const companionsOf = (entries: HierarchicalDataEntry[]): Set<string> => {
  const tables = new Set(entries.filter((e) => e.type === "TABLE").map((e) => e.label))
  return new Set(
    entries
      .filter(
        (e) =>
          isNumber(e) &&
          e.label.endsWith(VALUE_SUFFIX) &&
          tables.has(e.label.slice(0, -VALUE_SUFFIX.length))
      )
      .map((e) => e.label)
  )
}

/** At least one of `labels` can be one path segment: the variable can give a row or a column. */
const anyUsable = (labels: string[]): boolean => labels.some((l) => !unsafeLabel(l))
/**
 * A label that cannot be the first segment of a path: what `unsafeLabel`
 * refuses, and a leading `<` or `>`, which would follow the opening brackets
 * (`<<<25>>`) and has never been tried on the engine.
 */
const unsafeVariableLabel = (label: string): boolean => unsafeLabel(label) || /^\s*[<>]/.test(label)

/** The separator of codebook paths. */
const SEPARATOR = "//"

/**
 * The entries of one list below the top level that can be a segment of a
 * path: not the value field of a range question beside its TABLE, not a
 * name of the tab spec's own, and one segment the engine can take.
 */
const segmentsOf = (entries: HierarchicalDataEntry[]): HierarchicalDataEntry[] => {
  const companions = companionsOf(entries)
  return entries.filter(
    (e) => !companions.has(e.label) && !e.label.startsWith(OWN_PREFIX) && !unsafeLabel(e.label)
  )
}

const instance = (
  path: string,
  optionPaths: Record<string, string>,
  id = "",
  label = ""
): CatalogueInstance => ({
  id,
  // The parts of the id, as the questionnaire's instances have them.
  parts: id === "" ? [] : id.split(SEPARATOR),
  label,
  path,
  optionPaths,
  asked: null,
  coverage: null,
  offPath: 0,
  missingOnPath: 0,
  inData: true,
})

/**
 * The question text a datamap gave an entry (its FIELD_METADATA column), sent
 * as `metadata.field_metadata`. The datamap has one row per column of the
 * data, so the text of a multi or a grid sits on its leaves, not the TABLE:
 * the entry's own text, else the first one met below it, else "".
 */
const textOf = (e: HierarchicalDataEntry): string => {
  const own = (e as { metadata?: { field_metadata?: unknown } }).metadata?.field_metadata
  if (typeof own === "string" && own.trim() !== "") return own.trim()
  for (const k of childrenOf(e)) {
    const below = textOf(k)
    if (below) return below
  }
  return ""
}

const variable = (
  label: string,
  type: QuestionType,
  options: string[],
  instances: CatalogueInstance[],
  text: string,
  answers?: "single" | "multi"
): CatalogueQuestion => ({
  label,
  text,
  type,
  hidden: false,
  ordinal: false,
  options: options.map((o): CatalogueOption => ({ label: o, exception: false })),
  instances,
  // A table of numbers and a grid have one dimension, their rows.
  dimensions: instances.some((i) => i.parts.length > 0) ? ["Rows"] : [],
  ...(instances.some((i) => i.parts.length > 0) ? { rowsDimension: 0 } : {}),
  source: "data",
  ...(answers ? { answers } : {}),
})

/**
 * What the codebook says of the fields of a TABLE: `question_type`, the
 * library's own classification, sent by newer engines only ("single",
 * "multi", "grid-single", "grid-multi", "numeric", "text", "other").
 */
const answersOf = (e: HierarchicalDataEntry): "single" | "multi" | undefined => {
  const type = (e as { question_type?: unknown }).question_type
  if (type === "single" || type === "grid-single") return "single"
  if (type === "multi" || type === "grid-multi") return "multi"
  return undefined
}

/**
 * An entry at `path` (its full codebook path) as a catalogue question, or
 * null when it is none of the shapes offered. A yes or no field's one option
 * is the field's own label (the last segment), its path the whole path.
 */
const toVariable = (e: HierarchicalDataEntry, path: string): CatalogueQuestion | null => {
  if (isBool(e)) return variable(path, "select-many", [e.label], [instance(path, { [e.label]: path })], textOf(e))
  if (isNumber(e)) return variable(path, "number", [], [instance(path, {})], textOf(e))
  if (e.type !== "TABLE") return null
  // The value field of a range question is not one of the children.
  const all = childrenOf(e)
  const companions = companionsOf(all)
  const kids = all.filter((k) => !companions.has(k.label))
  if (kids.length === 0) return null
  const kidLabels = kids.map((k) => k.label)

  // Leaves, rows and cells the engine cannot take stay in the variable: the
  // compiler leaves each one out of its table with a sentence that names it.
  if (kids.every(isBool)) {
    if (!anyUsable(kidLabels)) return null
    const optionPaths = Object.fromEntries(kidLabels.map((l) => [l, `${path}//${l}`]))
    return variable(path, "select-many", kidLabels, [instance(path, optionPaths)], textOf(e), answersOf(e))
  }

  if (kids.every(isNumber)) {
    if (!anyUsable(kidLabels)) return null
    return variable(
      path,
      "grid-number",
      [],
      kidLabels.map((c) => instance(`${path}//${c}`, {}, c, c)),
      textOf(e)
    )
  }

  if (kids.every(isTableOfLeaves)) {
    // A grid: every row has the same leaves in the same order, as every row
    // of a questionnaire grid has the same options. Uneven rows are not
    // offered: a row's table would list the other rows' leaves as gone.
    const leaves = labelsOf(kids[0])
    const key = leaves.join("\n")
    if (kids.some((k) => labelsOf(k).join("\n") !== key)) return null
    if (!anyUsable(leaves) || !anyUsable(kidLabels)) return null
    const rows = kids.map((k) => {
      const rowPath = `${path}//${k.label}`
      const optionPaths = Object.fromEntries(leaves.map((l) => [l, `${rowPath}//${l}`]))
      return instance(rowPath, optionPaths, k.label, k.label)
    })
    return variable(path, "grid-many", leaves, rows, textOf(e), answersOf(e))
  }
  return null
}

/**
 * How a field is held in one row of a grid from siblings: "number" (INT or
 * REAL), or a TABLE of two or more yes or no fields keyed by its leaves in
 * order (the same leaves in another order are another shape, as for a
 * grid's rows). null for anything else: text, a yes or no field alone or in
 * a TABLE of its own (a table of one row gets no Totals block from the
 * engine, facts.md facts 1 and 7, so each one is tabulated as Yes and No on
 * its own), a deeper table.
 */
const fieldShape = (e: HierarchicalDataEntry): string | null =>
  isNumber(e)
    ? "number"
    : isTableOfLeaves(e) && childrenOf(e).length > 1
      ? JSON.stringify(labelsOf(e))
      : null

/** The rows that hold a field in one shape: each sibling's index and its entry. */
interface Held {
  at: number[]
  entries: HierarchicalDataEntry[]
}

/**
 * The grids whose rows are sibling containers. `entries` are the children
 * of the container at `path`, each with the variable it is (`offered`, null
 * when none); the siblings are the TABLEs among them that are no variable of
 * their own (not a table of yes or no fields, a table of numbers or a grid,
 * which already have their rows): containers of several kinds, one per
 * bank. Each field two or more of them hold in the same shape (a table of
 * yes or no fields with the same leaves, or a number) is one variable with a
 * row per sibling that holds it so: "Overall Brand NPS: score", rows "Bank
 * 1", "Bank 2", ... at "Overall Brand NPS//Bank 1//score", .... A sibling
 * without the field, or with it in another shape, is left out and counted.
 * When the siblings hold a field in two shapes, the one most of them hold
 * makes the grid (the first met on a tie). The label is the parent's path
 * and the field in words, never a path; the grid's `answers` is the one
 * every row's TABLE agrees on. Each field of each sibling is read once.
 */
const siblingGrids = (
  entries: HierarchicalDataEntry[],
  offered: (CatalogueQuestion | null)[],
  path: string
): CatalogueQuestion[] => {
  const labels = new Set<string>()
  // The first of two siblings with one label: a row id is unique in its grid.
  const rows = entries.filter((e, i) => {
    if (e.type !== "TABLE" || offered[i] || labels.has(e.label)) return false
    labels.add(e.label)
    return true
  })
  if (rows.length < 2) return []
  // Each field label, in the order first met, to the rows that hold it in
  // each shape; a sibling's second field with one label is not read.
  const byName = new Map<string, Map<string, Held>>()
  rows.forEach((r, i) => {
    const read = new Set<string>()
    segmentsOf(childrenOf(r)).forEach((f) => {
      if (read.has(f.label)) return
      read.add(f.label)
      let shapes = byName.get(f.label)
      if (!shapes) {
        shapes = new Map()
        byName.set(f.label, shapes)
      }
      const shape = fieldShape(f)
      if (shape === null) return
      const held = shapes.get(shape)
      if (held) {
        held.at.push(i)
        held.entries.push(f)
      } else shapes.set(shape, { at: [i], entries: [f] })
    })
  })
  const within = path.split(SEPARATOR).join(": ")
  return [...byName].flatMap(([name, shapes]) => {
    let held: Held = { at: [], entries: [] }
    shapes.forEach((h) => {
      if (h.at.length > held.at.length) held = h
    })
    if (held.at.length < 2) return []
    const label = `${within}: ${name}`
    const siblings = { container: path, field: name, leftOut: rows.length - held.at.length }
    const rowPath = (i: number): string => [path, rows[i].label, name].join(SEPARATOR)
    if (isNumber(held.entries[0])) {
      const cells = held.at.map((i) => instance(rowPath(i), {}, rows[i].label, rows[i].label))
      return [{ ...variable(label, "grid-number", [], cells, textOf(held.entries[0])), siblings }]
    }
    const leaves = labelsOf(held.entries[0])
    if (!anyUsable(leaves)) return []
    const grid = held.at.map((i) =>
      instance(
        rowPath(i),
        Object.fromEntries(leaves.map((l) => [l, `${rowPath(i)}${SEPARATOR}${l}`])),
        rows[i].label,
        rows[i].label
      )
    )
    const said = held.entries.map(answersOf)
    const answers = said.every((a) => a === said[0]) ? said[0] : undefined
    return [{ ...variable(label, "grid-many", leaves, grid, textOf(held.entries[0]), answers), siblings }]
  })
}

/**
 * Codebook entries that are not the questionnaire's, as catalogue questions
 * with `source: "data"`, in codebook order, walked to any depth: a TABLE that
 * is none of the shapes offered is not a variable, and the variables below
 * it take its place, each labelled by its full path. An offered shape is
 * never walked into (a grid's rows are its instances, a table of numbers'
 * cells its cells). `questionLabels` holds the label of every question of
 * the questionnaire (empty in a project without one).
 *
 * Left out: a question's label and all below it, the ` - value` field the
 * engine adds next to a question, the value field of a range question (a
 * number `<L> - value` beside a TABLE `<L>`, at every depth), a name of the
 * tab spec's own (`__ts_`), the console's helper fields, a segment that
 * cannot be one in a path (for the first one also a leading `<` or `>`), and
 * text and string fields. A variable is offered when at least one of its
 * leaves (or rows, or cells) can be used; the others stay in it, and the
 * compiler names them.
 *
 * A walked container's grids from siblings (`siblingGrids`) come first, then
 * the variables below it. Such a grid is left out when its label is taken:
 * by a top-level entry (a codebook path), a question, another variable or an
 * earlier grid from siblings.
 */
export const dataVariablesOf = (
  codebook: HierarchicalDataEntry[] | null,
  questionLabels: Set<string>
): CatalogueQuestion[] => {
  if (!codebook) return []
  const isQuestionField = (label: string): boolean =>
    questionLabels.has(label) ||
    (label.endsWith(VALUE_SUFFIX) && questionLabels.has(label.slice(0, -VALUE_SUFFIX.length)))
  const top = codebook.filter(isEntry)
  const topCompanions = companionsOf(top)
  const kept = top.filter(
    (e) =>
      !topCompanions.has(e.label) &&
      !e.label.startsWith(OWN_PREFIX) &&
      !isQuestionField(e.label) &&
      !CONSOLE_HELPERS.includes(e.label) &&
      !unsafeVariableLabel(e.label)
  )
  // `entries` are the children of the container at `parent` (null: the top
  // level, which is no container); its grids from siblings come first.
  const walk = (entries: HierarchicalDataEntry[], parent: string | null): CatalogueQuestion[] => {
    const paths = entries.map((e) => (parent === null ? e.label : `${parent}${SEPARATOR}${e.label}`))
    const offered = entries.map((e, i) => toVariable(e, paths[i]))
    const grids = parent === null ? [] : siblingGrids(entries, offered, parent)
    return [
      ...grids,
      ...entries.flatMap((e, i): CatalogueQuestion[] => {
        const found = offered[i]
        if (found) return [found]
        return e.type === "TABLE" ? walk(segmentsOf(childrenOf(e)), paths[i]) : []
      }),
    ]
  }
  const found = walk(kept, null)
  if (!found.some((q) => q.siblings)) return found
  const taken = new Set([
    ...top.map((e) => e.label),
    ...questionLabels,
    ...found.flatMap((q) => (q.siblings ? [] : [q.label])),
  ])
  return found.filter((q) => {
    if (!q.siblings) return true
    if (taken.has(q.label)) return false
    taken.add(q.label)
    return true
  })
}

/**
 * A variable of the data by its parts, for the lists that show it: `name`,
 * the last segment of its codebook path (the part to set in bold); `within`,
 * the containers above it in words ("Overall Brand NPS: Bank 1"), null at the
 * top level; `path`, its whole label, for the tooltip. A grid from siblings
 * is named by the field its rows share ("score") within their parent
 * ("Overall Brand NPS"); its `path` is its label, which is no codebook path.
 */
export const dataVariableParts = (
  q: CatalogueQuestion
): { name: string; within: string | null; path: string } => {
  if (q.siblings)
    return {
      name: q.siblings.field,
      within: q.siblings.container.split(SEPARATOR).join(": "),
      path: q.label,
    }
  const parts = q.label.split(SEPARATOR)
  return {
    name: parts[parts.length - 1],
    within: parts.length > 1 ? parts.slice(0, -1).join(": ") : null,
    path: q.label,
  }
}

/**
 * One yes or no field of the data: a top-level BOOL (its one option is the
 * field itself, one path for both) or a TABLE whose only child is one BOOL
 * leaf. A table of one row gets no base from the engine, so its table has the
 * rows Yes and No instead.
 */
export const isYesNoField = (q: CatalogueQuestion): boolean =>
  q.source === "data" &&
  q.type === "select-many" &&
  q.options.length === 1 &&
  q.instances.length === 1

const count = (n: number, one: string, many: string): string =>
  n === 1 ? `1 ${one}` : `${n} ${many}`

const shapeWords = (q: CatalogueQuestion): string => {
  switch (q.type) {
    case "number":
      return "number"
    case "grid-number":
      return count(q.instances.length, "number", "numbers")
    case "grid-many":
      return count(q.instances.length, "row", "rows")
    default:
      return isYesNoField(q) ? "yes or no" : count(q.options.length, "category", "categories")
  }
}

/**
 * One short phrase for the picker: "yes or no", "4 categories", "number",
 * "3 rows", "3 numbers"; a grid from siblings also counts the siblings left
 * out of it ("4 rows, 1 left out").
 */
export const describeDataVariable = (q: CatalogueQuestion): string => {
  const words = shapeWords(q)
  const leftOut = q.siblings?.leftOut ?? 0
  return leftOut > 0 ? `${words}, ${leftOut} left out` : words
}
